import {
  CommunicationService,
  calculateStepCount,
} from '@carboncopyco/mimesis-shared';
import { Replayer } from '@carboncopyco/rrweb';
import type {
  eventWithTime,
  metaEvent,
  playerMetaData,
} from '@carboncopyco/rrweb-types';
import {
  type FrameType,
  type IAddStepOptions,
  type MediaContent,
  type RRWebEvent,
  type Tour,
  type TourAspectRatio,
  type TourFrame,
  type TourSettings,
  type TourStep,
  TourStepEngineIncomingMessageType,
} from '@carboncopyco/types';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import { ColorHex, LoadingDots } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  type DependencyList,
  type ForwardedRef,
  type RefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useScaleDownToWindow } from '../hooks';
import type { UseIncrementalNavigationReturnType } from '../hooks/replayer';
import { useStopPlayingMedia } from '../hooks/replayer/useStopPlayingMedia';
import usePrevious from '../hooks/usePrevious';
import { applyClientSpecificFixes } from '../utils/clientSpecificFixes';
import type { frameSeparation } from '../utils/eventIndex';
import expandBody from '../utils/expandBody';
import { getScreenResolutionTypeFromWidth } from '../utils/getScreenResolutionTypeFromWidth';
import {
  getFirstEvent,
  getSeekTime,
  isValidAspectRatio,
} from '../utils/replayUtils';
import { CoverImageLoader } from './CoverImageLoader';
import DeviceWrapper from './DeviceWrapper';
import { NavigationMenu } from './NavigationMenu/NavigationMenu';

if (typeof window !== 'undefined') {
  window.SC_DISABLE_SPEEDY = true;
}

const REPLAY_SPEED = 360;
const engineLogger = scopedLogger(Scope.TOUR_ENGINE);

interface PlayerProps {
  events: eventWithTime[];
  editorMode?: boolean;
  newPostMessage?: boolean;
  fileServiceApiToken?: string;
  isBuffering?: boolean;
  tourAspectRatio?: TourAspectRatio;
  frameChunk?: frameSeparation;
  selectFrameByIndex?: (frameIndex: number) => void;
  mimesisStepEngineUrl: string;
  fileServiceDynamicLink: string;
  isDebugMode: boolean;
  activeFrameId?: number;
  activeStep: TourStep;
  playableFrames: TourFrame[];
  isAnyFrameScreenshot: boolean;
  isLoading?: boolean;
  tourSettings: TourSettings;
  onPauseHandler?: () => void;
  onPlayHandler?: () => void;
  incrementalStepNavigation: UseIncrementalNavigationReturnType;
  onRestartHandler: () => void;
  initFrameDeps?: DependencyList;
  activeTabId?: number;
  organizationId?: string;
  isTourLastStep: boolean;
  isTourFirstStep: boolean;
  rootElement: RefObject<HTMLElement>;
}

export interface TourPlayerRef {
  replayer: RefObject<any | null>;
  showStep: ({
    step,
    tourSettings,
    isEditor,
    isStepEditable,
    isTourFirstStep,
    isTourLastStep,
    frames,
    isPositionWarningActive,
  }: ShowStepParams) => void;
  removeStep: () => void;
  // TODO:
  //  take care of this type when migrate from bonfire
  setInspectMode: ({
    frameType,
    addStepOptions,
  }?: {
    frameType?: FrameType;
    addStepOptions?: IAddStepOptions;
  }) => void;
  cancelInspectMode: () => void;
  isPlaying: boolean;
  createdReplayer: boolean;
  removeHighlight: () => void;
  playTourAtTime: (
    time: number,
    event: eventWithTime,
    screenshotUrl?: string
  ) => void;
  setIsApplyingConfigChanges: (isApplying: boolean) => void;
  setHasStepConfigUpdates: (hasStepConfigUpdates: boolean) => void;
  onTooltipMediaUpload: (
    uploadProgress: number,
    fileServiceUrl: string,
    type: MediaContent['type']
  ) => void;
}

interface ShowStepParams {
  step: TourStep;
  tourSettings: Tour['tourSettings'];
  isStepEditable?: boolean | undefined;
  isTourFirstStep?: boolean | undefined;
  isTourLastStep?: boolean | undefined;
  isEditor?: boolean | undefined;
  frames?: TourFrame[] | undefined;
  frameId?: number;
  isPositionWarningActive: boolean;
  frameType: TourFrame['frameType'];
}

const communicationService = new CommunicationService({
  ignoreDeviceContext: true,
});

export const TourPlayer = forwardRef<TourPlayerRef, PlayerProps>(
  (
    {
      onPlayHandler,
      onPauseHandler,
      incrementalStepNavigation,
      onRestartHandler,
      events,
      editorMode,
      isBuffering = false,
      fileServiceApiToken,
      tourAspectRatio = 'responsive',
      frameChunk,
      selectFrameByIndex,
      mimesisStepEngineUrl,
      fileServiceDynamicLink,
      isDebugMode,
      activeFrameId,
      activeStep,
      playableFrames,
      isAnyFrameScreenshot,
      isLoading = false,
      tourSettings,
      initFrameDeps,
      activeTabId,
      organizationId,
      isTourLastStep,
      isTourFirstStep,
      rootElement,
    }: PlayerProps,
    ref: ForwardedRef<TourPlayerRef>
  ) => {
    const defaultTime = {
      startTime: 0,
      endTime: 0,
      totalTime: 0,
    };

    const replayer = useRef<Replayer | null>(null);
    const frameRef = useRef<HTMLDivElement | null>(null);
    const wrapper = useRef<HTMLDivElement | null>(null);
    const timer = useRef<number | null>(null);
    const isPlaying = useRef<boolean>(true);
    const { search } = useLocation();
    const [playerIframe, setPlayerIframe] = useState<
      HTMLIFrameElement | undefined
    >(undefined);
    const [replayerWrapper, setReplayerWrapper] = useState<
      HTMLElement | undefined
    >(undefined);
    const [createdReplayer, setCreatedReplayer] = useState<boolean>(false);
    const [meta, setMeta] = useState<playerMetaData>(defaultTime);
    const [isContentLoaded, setIsContentLoaded] = useState<boolean>(false);
    const [isPaused, setIsPaused] = useState(false);
    const [requestedShowStepParams, setRequestedShowStepParams] =
      useState<ShowStepParams | null>(null);

    const aspectRatio: TourAspectRatio = useMemo(() => {
      if (
        tourSettings?.screenResolution?.type &&
        tourSettings?.screenResolution?.type !== 'responsive'
      ) {
        return 'fixedWidthAndHeight';
      }
      return isValidAspectRatio(tourAspectRatio)
        ? tourAspectRatio
        : 'responsive';
    }, [tourAspectRatio, tourSettings?.screenResolution]);

    const isFixedAspectRatio =
      aspectRatio === ('fixedWidthAndHeight' as TourAspectRatio);

    const scalleDivRef = useRef<HTMLDivElement | null>(null);

    const wrapperType = tourSettings?.background?.wrapperType;

    const firstEvent = getFirstEvent(events as eventWithTime[]);

    const initialWidth = (firstEvent.data as any).width;
    const initialHeight = (firstEvent.data as any).height;

    useStopPlayingMedia({ activeTabId, playerIframe });

    useScaleDownToWindow({
      aspectRatio,
      targetElement: scalleDivRef.current,
      playerIframe: playerIframe,
      initialSize: {
        width: initialWidth,
        height: initialHeight,
      },
      containerRef: wrapper,
      isAnyFrameScreenshot: isAnyFrameScreenshot,
      replayerWrapper,
      activeTabId,
      isSpotlightActive: activeStep?.config?.tooltip.spotlightOptions.isActive,
    });

    const screenResolutionType = getScreenResolutionTypeFromWidth(initialWidth);

    const stopTimer = () => {
      if (timer.current) {
        cancelAnimationFrame(timer.current);
      }
    };

    const updateTime = () => {
      timer.current = requestAnimationFrame(updateTime);
    };

    const pause = () => {
      isPlaying.current = false;
      setIsPaused(true);
      replayer.current?.pause();
    };

    const seek = (
      time?: number,
      event?: eventWithTime,
      screenshotUrl?: string
    ) => {
      applyClientSpecificFixes(
        (events[0] as metaEvent)?.data?.href,
        replayer.current?.iframe?.contentDocument,
        'seek'
      );

      replayer.current.enableInteract();

      if (editorMode) {
        communicationService.postMessage({
          message: {
            type: TourStepEngineIncomingMessageType.removeHighlight,
          },
          targetWindow: playerIframe?.contentWindow,
        });
        engineLogger.info('Removing highlight');
      }

      onPlayHandler?.();

      if (screenshotUrl) {
        replayer.current?.playScreenshot(
          `${screenshotUrl}?token=${fileServiceApiToken}`
        );
        /*
          Screenshot is a custom function in rrweb and does not emit play/pause
          like regular events do
        */
        if (editorMode) {
          expandBody(rootElement);
        }
        onPauseHandler?.();
      } else {
        time = getSeekTime(time, event);
        replayer.current?.play(time, true);
        setIsPaused(false);
        isPlaying.current = true;
      }
    };

    const onFinish = () => {
      communicationService.postMessage({
        message: {
          type: TourStepEngineIncomingMessageType.tourEnded,
        },
        targetWindow: playerIframe?.contentWindow,
      });
      engineLogger.info('Tour ended');
    };

    const flags = useFlags();

    const preloadPlayer = useRef<Replayer | null>(null);

    const preloadTourInBackground = (preloadFrame: HTMLElement) => {
      preloadFrame.style.position = 'absolute';
      preloadFrame.style.top = '-9999px';
      preloadFrame.id = 'preload-frame';
      document.body.appendChild(preloadFrame);

      preloadPlayer.current = new Replayer(events as eventWithTime[], {
        root: preloadFrame,
        useVirtualDom: false,
        mouseTail: false,
        UNSAFE_replayCanvas: true,
        skipInactive: true,
        triggerFocus: false,
        speed: 10,
        loadTimeout: 1000,
        tourStepEngineScriptUrl: mimesisStepEngineUrl,
        fileServiceDynamicLink,
        fileServiceApiToken,
        showWarning: false,
        showDebug: false,
        pauseAnimation: false,
        shouldPreload: true,
      });
      preloadPlayer.current.on('finish', destroyPreload);
      preloadPlayer.current.play();
    };

    const destroyPreload = () => {
      try {
        preloadPlayer.current?.destroy();
      } catch (e) {
        console.error('Error destroying preload player', e);
      }
      document.getElementById('preload-frame')?.remove();
      // remove reference to free up memory
      preloadPlayer.current = null;
    };

    const createReplayer = (frame: HTMLElement) => {
      if (flags.toursPreloadInBackground) {
        preloadTourInBackground(frame.cloneNode(true) as HTMLElement);
      }

      replayer.current = new Replayer(events as eventWithTime[], {
        root: frame,
        useVirtualDom: false,
        mouseTail: false,
        UNSAFE_replayCanvas: true,
        skipInactive: true,
        triggerFocus: false,
        speed: REPLAY_SPEED,
        loadTimeout: 1000,
        tourStepEngineScriptUrl: mimesisStepEngineUrl,
        fileServiceDynamicLink,
        fileServiceApiToken,
        showWarning: isDebugMode,
        showDebug: isDebugMode,
        pauseAnimation: false,
        shouldPreload: true,
      })
        .on('event-cast', (event: RRWebEvent) => {
          if (event.frameSeparator && isPlaying.current) {
            pause();
            onPauseHandler?.();
            const firstEventData = (events[0] as metaEvent).data;
            applyClientSpecificFixes(
              firstEventData.href,
              replayer.current?.iframe?.contentDocument,
              'event-cast'
            );
            if (editorMode) {
              expandBody(rootElement);
            }
          }

          const lastEvent = events[events.length - 1];
          if (
            lastEvent.timestamp === event.timestamp &&
            event.frameReset === false &&
            event.frameSeparator
          ) {
            handleFinishReplay();
          }
        })
        .on(
          'load-stylesheet-start',
          () => isContentLoaded && setIsContentLoaded(false)
        )
        .on(
          'load-stylesheet-end',
          () => !isContentLoaded && setIsContentLoaded(true)
        );
      setCreatedReplayer(true);
    };

    const handleFinishReplay = () => {
      if (isPlaying.current) {
        pause();
        onPauseHandler?.();
      }
      onFinish();
    };

    const wrapperTypePrev = usePrevious(wrapperType);
    const isFixedAspectRatioPrev = usePrevious(isFixedAspectRatio);
    const eventsLengthPrev = usePrevious(events.length);

    useEffect(() => {
      if (
        (frameRef.current && (!createdReplayer || initFrameDeps)) ||
        (frameRef.current && wrapperType !== wrapperTypePrev) ||
        (frameRef.current && isFixedAspectRatio !== isFixedAspectRatioPrev) ||
        (frameRef.current && events.length !== eventsLengthPrev)
      ) {
        stopTimer();
        createReplayer(frameRef.current);
        updateTime();
        setPlayerIframe(replayer.current?.iframe);
        setReplayerWrapper(frameRef.current?.children[0] as HTMLElement);

        const playerTime = frameChunk?.playerTime;
        const event = frameChunk?.event;
        const screenshotUrl = frameChunk?.screenshotUrl;

        seek(0); // this seek is needed for recreating DOM three
        setTimeout(() => {
          seek(playerTime, event, screenshotUrl);
        }, 0);
        if (!isBuffering && replayer.current) {
          const meta = replayer.current?.getMetaData();
          setMeta(meta);
        }
        wrapper.current?.focus();
        engineLogger.info('Replayer created');
      }
      return () => {
        stopTimer();
        replayer.current?.pause();
        replayer.current?.destroy();
        destroyPreload();
      };
    }, [
      frameRef,
      wrapperType,
      events.length,
      isFixedAspectRatio,
      ...(initFrameDeps || []),
    ]);

    useEffect(() => {
      stopTimer();
      if (!isPaused && meta.totalTime > 0) {
        updateTime();
        return () => stopTimer();
      }
    }, [isPaused, meta]);

    useEffect(() => {
      if (!isContentLoaded) {
        const isContentLoadedFallback = setTimeout(
          () => setIsContentLoaded(true),
          2000
        );
        return () => {
          clearTimeout(isContentLoadedFallback);
        };
      }
    }, [isContentLoaded]);

    useEffect(() => {
      const queryParams = new URLSearchParams(search);
      const selectedFrameIndex = queryParams.get('selectedFrameIndex');
      if (selectedFrameIndex && isContentLoaded) {
        selectFrameByIndex?.(Number.parseInt(selectedFrameIndex));
      }
    }, [search, isContentLoaded]);

    useEffect(() => {
      if (isContentLoaded && requestedShowStepParams) {
        handleShowStep(requestedShowStepParams);
        setRequestedShowStepParams(null);
      }
    }, [isContentLoaded, requestedShowStepParams]);

    const handleShowStep = ({
      step,
      tourSettings,
      isStepEditable,
      isTourFirstStep,
      isTourLastStep,
      isEditor,
      frames,
      isPositionWarningActive,
      frameType,
    }: ShowStepParams) => {
      communicationService.postMessage({
        message: {
          type: isEditor
            ? TourStepEngineIncomingMessageType.editTourStep
            : TourStepEngineIncomingMessageType.showTourStep,
          payload: {
            step,
            tourSettings,
            editorMode,
            isStepEditable,
            isTourFirstStep,
            isTourLastStep,
            frameId: activeFrameId,
            frames,
            frameType,
            flags: { ...flags }, // for some reason in desktop flags are received as Proxy(Object), and becase of that this is needed to get normal object
            isPositionWarningActive,
            organizationId,
            fileServiceApiToken,
          },
        },
        targetWindow: playerIframe?.contentWindow,
      });
      engineLogger.info(`${isEditor ? 'Editing' : 'Showing'} tour step`, step);
      if (
        editorMode &&
        step.config?.tooltip.balloon.layout.relation !== 'page'
      ) {
        communicationService.postMessage({
          message: {
            type: TourStepEngineIncomingMessageType.highlightElement,
            payload: { step, frameType },
          },
          targetWindow: playerIframe?.contentWindow,
        });
        engineLogger.info('Highlighting element');
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        replayer,
        removeStep: () => {
          communicationService.postMessage({
            message: {
              type: TourStepEngineIncomingMessageType.removeTourStep,
            },
            targetWindow: playerIframe?.contentWindow,
          });
          engineLogger.info('Removing tour step');
        },
        showStep: (showStepParams: ShowStepParams) => {
          if (isContentLoaded) {
            handleShowStep(showStepParams);
            setRequestedShowStepParams(null);
          } else {
            setRequestedShowStepParams(showStepParams);
          }
        },
        setInspectMode: ({ frameType, addStepOptions }) => {
          communicationService.postMessage({
            message: {
              type: TourStepEngineIncomingMessageType.setInspectMode,
              payload: {
                frameType,
                addStepOptions,
              },
            },
            targetWindow: playerIframe?.contentWindow,
          });
          engineLogger.info('Setting inspect mode to add step', addStepOptions);
        },
        cancelInspectMode: () => {
          communicationService.postMessage({
            message: {
              type: TourStepEngineIncomingMessageType.cancelInspectMode,
            },
            targetWindow: playerIframe?.contentWindow,
          });
          engineLogger.info('Cancelling inspect mode');
        },
        removeHighlight: () => {
          communicationService.postMessage({
            message: {
              type: TourStepEngineIncomingMessageType.removeHighlight,
            },
            targetWindow: playerIframe?.contentWindow,
          });
          engineLogger.info('Removing highlight');
        },
        playTourAtTime: (
          time: number,
          event: eventWithTime,
          screenshotUrl?: string
        ) => {
          seek(time, event, screenshotUrl);
        },
        setIsApplyingConfigChanges: (isApplying: boolean) => {
          communicationService.postMessage({
            message: {
              type: TourStepEngineIncomingMessageType.setIsApplyingConfigChanges,
              payload: isApplying,
            },
            targetWindow: playerIframe?.contentWindow,
          });
          engineLogger.info('Setting isApplyingConfigChanges', { isApplying });
        },
        setHasStepConfigUpdates: (hasStepConfigUpdates: boolean) => {
          communicationService.postMessage({
            message: {
              type: TourStepEngineIncomingMessageType.setHasStepConfigUpdates,
              payload: hasStepConfigUpdates,
            },
            targetWindow: playerIframe?.contentWindow,
          });
          engineLogger.info('Setting hasStepConfigUpdates', {
            hasStepConfigUpdates,
          });
        },
        onTooltipMediaUpload: (
          uploadProgress: number,
          fileServiceUrl: string,
          type: MediaContent['type']
        ) => {
          communicationService.postMessage({
            message: {
              type: TourStepEngineIncomingMessageType.onMediaUpload,
              payload: {
                uploadProgress,
                fileServiceUrl,
                type,
              },
            },
            targetWindow: playerIframe?.contentWindow,
          });
          engineLogger.info('Tooltip media upload', {
            uploadProgress,
            fileServiceUrl,
            type,
          });
        },
        isPlaying: isPlaying.current,
        createdReplayer,
      }),
      [replayer, seek]
    );

    const stepCount = useMemo(() => {
      return calculateStepCount(activeStep, playableFrames);
    }, [activeStep, playableFrames]);

    const showLoader = !isContentLoaded || isLoading;

    return (
      <div
        className={classNames([
          'h-full w-full flex items-center justify-center',
          isFixedAspectRatio ? 'flex' : 'block',
        ])}
      >
        <div
          id="wrapper"
          ref={wrapper}
          className={classNames([
            'w-full h-full flex items-center justify-center',
          ])}
        >
          <div
            id="scalleDivRef"
            ref={scalleDivRef}
            className={classNames(isFixedAspectRatio ? '' : 'w-full h-full')}
          >
            <DeviceWrapper
              apply={isFixedAspectRatio}
              wrapperType={wrapperType}
              screenResolutionType={screenResolutionType}
              hideBorders={showLoader}
            >
              <>
                {showLoader && (
                  <div className="w-full h-full flex justify-center items-center bg-transparent">
                    {flags.toursCoverImageLoader &&
                    aspectRatio === 'fixedWidthAndHeight' &&
                    playableFrames[0]?.frameScreenshotUrl ? (
                      <CoverImageLoader
                        url={playableFrames[0].frameScreenshotUrl}
                        fileServiceApiToken={fileServiceApiToken}
                      />
                    ) : (
                      <LoadingDots color={ColorHex.natural600} />
                    )}
                  </div>
                )}
                <div
                  id="frameRef"
                  ref={frameRef}
                  className={classNames([
                    'h-full items-center justify-center',
                    isFixedAspectRatio ? 'flex' : 'block',
                    showLoader ? 'hidden' : 'block',
                  ])}
                />
              </>
            </DeviceWrapper>
          </div>

          <NavigationMenu
            stepCount={stepCount}
            isTourFirstStep={isTourFirstStep}
            isTourLastStep={isTourLastStep}
            tourSettings={tourSettings}
            onNextClick={() => incrementalStepNavigation('next')}
            onBackClick={() => incrementalStepNavigation('back')}
            onRestartClick={onRestartHandler}
          />
        </div>
      </div>
    );
  }
);

TourPlayer.displayName = 'TourPlayer';
